import { useCallback, useEffect, useRef } from 'react';

import { DEFAULT_EDGE_PADDING } from '../../constants';
import {
  checkIfValidCenterRadius,
  convertMilesToMeters,
  getCircleRadius,
} from './helpers';
import {
  CIRCLE_FILL_COLOR,
  CIRCLE_STROKE_COLOR,
  CIRCLE_STROKE_WIDTH,
} from './useMapCircleRadius.constants';
import type { UseMapCircleRadiusParams } from './useMapCircleRadius.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useMapCircleRadius = (params: UseMapCircleRadiusParams) => {
  const { variation, circleRadius } = params;

  // ─── Refs ────────────────────────────────────────────────────────────

  const mapRef = variation === 'web' ? params.googleMapRef : null;
  const circleRef = useRef<google.maps.Circle | null>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const cleanUpCircleRef = useCallback(() => {
    circleRef.current?.setMap(null);
    circleRef.current = null;
  }, []);

  const cleanUpCircleRefOnRemove = useCallback(() => {
    const hasRadius = mapRef && circleRadius;
    const isAlreadyCleanedUp = !circleRef.current;

    if (hasRadius || isAlreadyCleanedUp) {
      return;
    }

    cleanUpCircleRef();
  }, [circleRadius, cleanUpCircleRef, mapRef]);

  const createCircle = useCallback(() => {
    if (!circleRadius) return;

    const { centerLat, centerLng } = circleRadius;

    // Get radius (calculated from bounds if not provided)
    const radiusInMiles = getCircleRadius(circleRadius);

    if (!radiusInMiles) return;

    circleRef.current = new google.maps.Circle({
      center: { lat: centerLat, lng: centerLng },
      radius: convertMilesToMeters(radiusInMiles),
      strokeColor: CIRCLE_STROKE_COLOR,
      strokeWeight: CIRCLE_STROKE_WIDTH,
      fillColor: CIRCLE_FILL_COLOR,
      map: mapRef,
    });
  }, [circleRadius, mapRef]);

  const updateCircle = useCallback(() => {
    if (!circleRadius) return;

    const { centerLat, centerLng } = circleRadius;

    // Get radius (calculated from bounds if not provided)
    const radiusInMiles = getCircleRadius(circleRadius);

    if (!radiusInMiles) return;

    circleRef.current?.setCenter({ lat: centerLat, lng: centerLng });
    circleRef.current?.setRadius(convertMilesToMeters(radiusInMiles));
  }, [circleRadius]);

  const fitMapToCircleBounds = useCallback(() => {
    if (!mapRef || !circleRef.current) return;

    const bounds = circleRef.current.getBounds();

    if (!bounds) return;

    const padding = DEFAULT_EDGE_PADDING;
    const edgePadding = { top: padding, right: padding, bottom: padding, left: padding }; // prettier-ignore

    mapRef.fitBounds(bounds, edgePadding);
  }, [mapRef]);

  const generateCircle = useCallback(() => {
    if (!circleRadius) return;

    const googleMaps = typeof google === 'undefined' ? undefined : google?.maps;
    const currentCircle = circleRef.current;
    const hasRequiredApis = Boolean(googleMaps?.Circle && googleMaps?.LatLngBounds); // prettier-ignore
    const hasValidCircleRadius = checkIfValidCenterRadius(circleRadius);

    // ─── Invalid Coordinates ─────────────────────────────────────

    if (!hasValidCircleRadius) {
      cleanUpCircleRef();

      return;
    }

    // ─── Missing Required APIs ───────────────────────────────────

    if (!hasRequiredApis) {
      return;
    }

    // ─── Create or Update Circle ─────────────────────────────────

    try {
      if (currentCircle) {
        updateCircle();
        fitMapToCircleBounds();

        return;
      }

      createCircle();
      fitMapToCircleBounds();
    } catch {
      // Silently fail if circle creation or bounds fitting fails
    }
  }, [
    circleRadius,
    cleanUpCircleRef,
    createCircle,
    fitMapToCircleBounds,
    updateCircle,
  ]);

  // ─── Effects ───────────────────────────────────────────────────────────

  useEffect(cleanUpCircleRefOnRemove, [cleanUpCircleRefOnRemove]);
  useEffect(generateCircle, [generateCircle]);
};
